import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { port } from '../../App'
import { Modal } from 'react-bootstrap'
import { toast } from 'react-toastify'

const RecordPaymentModal = ({ show, setshow, cycle, onSuccess }) => {
    const [paymentType, setPaymentType] = useState('client') // 'client' or 'candidate'
    const [paymentData, setPaymentData] = useState({
        payment_amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_reference: ''
    })
    const [loading, setLoading] = useState(false)

    // Derived values based on paymentType
    const isCandidate = paymentType === 'candidate'
    const isContractBasis = cycle?.is_contract_basis
    const targetTotal = isCandidate 
        ? parseFloat(cycle?.candidate_payout_amount || 0) 
        : parseFloat(cycle?.billing_amount || 0)
    const targetPaid = isCandidate 
        ? parseFloat(cycle?.candidate_paid_amount || 0) 
        : parseFloat(cycle?.paid_amount || 0)
    const currentBalance = targetTotal - targetPaid

    // Reset form when cycle or paymentType changes
    useEffect(() => {
        if (cycle) {
            setPaymentData({
                payment_amount: currentBalance > 0 ? currentBalance.toFixed(2) : '',
                payment_date: new Date().toISOString().split('T')[0],
                payment_reference: ''
            })
        }
    }, [cycle, paymentType])

    const handleTypeChange = (type) => {
        setPaymentType(type)
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        const amt = parseFloat(paymentData.payment_amount)
        if (!amt || amt <= 0) {
            toast.error("Enter a valid amount.")
            return
        }
        if (amt > currentBalance) {
            toast.error(`Amount cannot exceed remaining balance of ₹${currentBalance.toLocaleString()}`)
            return
        }

        setLoading(true)
        try {
            const payload = {
                ...paymentData,
                payment_type: paymentType // 'client' or 'candidate'
            }
            await axios.patch(`${port}/root/cms/billing-payment/${cycle.id}/`, payload)
            
            const typeLabel = isCandidate ? "Candidate Payout" : "Client Payment"
            toast.success(`✅ ${typeLabel} of ₹${amt.toLocaleString()} recorded!`)
            
            setshow(false)
            if (onSuccess) onSuccess()
        } catch (error) {
            const errMsg = error?.response?.data?.error || "Failed to record payment."
            toast.error(errMsg)
        } finally {
            setLoading(false)
        }
    }

    if (!cycle) return null

    return (
        <Modal show={show} onHide={() => setshow(false)} centered size="md" className="poppins">
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="fw-bold text-gray-800 text-lg">
                    Record Transaction
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-2">
                <div className="px-1 poppins">
                    {/* Compact Placement Info */}
                    <div className="mb-4 p-2 bg-slate-50 rounded border border-slate-100">
                        <div className="flex justify-between items-center px-2">
                            <div>
                                <p className="text-[10px] text-gray-400 fw-bold uppercase mb-0">Candidate</p>
                                <p className="text-sm fw-bold text-gray-700 mb-0">{cycle.candidate_name}</p>
                            </div>
                            <div className="text-right">
                                <p className="text-[10px] text-gray-400 fw-bold uppercase mb-0">Month</p>
                                <p className="text-sm fw-bold text-gray-700 mb-0">{cycle.cycle_month}</p>
                            </div>
                        </div>
                    </div>

                    {/* Transaction Type Selection (Only for contract basis) */}
                    {isContractBasis && (
                        <div className="mb-4">
                            <label className="text-[11px] fw-bold text-gray-500 uppercase tracking-wider mb-2 block">Transaction Type</label>
                            <div className="flex bg-slate-100 p-1 rounded">
                                <button
                                    type="button"
                                    onClick={() => handleTypeChange('client')}
                                    className={`flex-1 py-1.5 rounded text-[11px] fw-bold transition-all ${
                                        !isCandidate ? 'bg-white text-blue-600 shadow-sm border border-slate-100' : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    Client Income
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleTypeChange('candidate')}
                                    className={`flex-1 py-2 rounded text-[11px] fw-bold transition-all ${
                                        isCandidate ? 'bg-white text-orange-600 shadow-sm border border-slate-100' : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    Candidate Payout
                                </button>
                            </div>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-[11px] fw-bold text-gray-500 uppercase block mb-1">
                                    {isCandidate ? 'Total Salary' : 'Billed Amount'}
                                </label>
                                <div className="text-lg fw-bold text-gray-800 bg-slate-50 p-2 rounded border border-dashed text-center">
                                    ₹{parseFloat(targetTotal).toLocaleString()}
                                </div>
                            </div>
                            <div>
                                <label className="text-[11px] fw-bold text-gray-500 uppercase block mb-1">Remaining</label>
                                <div className={`text-lg fw-bold p-2 rounded border border-dashed text-center ${currentBalance > 0 ? 'text-red-600 bg-red-50/30' : 'text-green-600 bg-green-50/30'}`}>
                                    ₹{currentBalance.toLocaleString()}
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className="text-[11px] fw-bold text-gray-500 uppercase block mb-1">
                                {isCandidate ? 'Payout Amount' : 'Amount Received'}
                            </label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 fw-bold">₹</span>
                                <input
                                    type="number"
                                    required
                                    className="w-full pl-7 pr-4 py-2.5 border border-gray-200 rounded text-sm outline-none focus:border-blue-400 transition-all fw-bold shadow-sm"
                                    placeholder="0.00"
                                    value={paymentData.payment_amount}
                                    onChange={(e) => setPaymentData({ ...paymentData, payment_amount: e.target.value })}
                                    max={currentBalance}
                                    min="1"
                                />
                            </div>
                            <p className="text-[10px] text-gray-400 mt-1 italic">Maximum allowed: ₹{currentBalance.toLocaleString()}</p>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-[11px] fw-bold text-gray-500 uppercase block mb-1">Payment Date</label>
                                <input
                                    type="date"
                                    required
                                    className="w-full px-2 py-2 border border-gray-200 rounded text-[13px] outline-none focus:border-blue-400 transition-all shadow-sm"
                                    value={paymentData.payment_date}
                                    onChange={(e) => setPaymentData({ ...paymentData, payment_date: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="text-[11px] fw-bold text-gray-500 uppercase block mb-1">Reference No</label>
                                <input
                                    type="text"
                                    className="w-full px-3 py-2 border border-gray-200 rounded text-[13px] outline-none focus:border-blue-400 transition-all shadow-sm"
                                    placeholder="TXN / CHQ / UTR"
                                    value={paymentData.payment_reference}
                                    onChange={(e) => setPaymentData({ ...paymentData, payment_reference: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="pt-2 flex gap-3">
                            <button
                                type="button"
                                onClick={() => setshow(false)}
                                className="flex-1 px-3 py-2 border border-gray-200 rounded text-[11px] fw-bold uppercase tracking-wider text-gray-500 hover:bg-gray-50 transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading || currentBalance <= 0}
                                className={`bluebtn flex-[2] py-2 rounded text-[11px] fw-bold uppercase tracking-wider transition-all shadow-sm ${
                                    loading ? 'opacity-70 cursor-not-allowed' : ''
                                }`}
                            >
                                {loading ? 'Processing...' : `Confirm ${isCandidate ? 'Salary Payout' : 'Income Payment'}`}
                            </button>
                        </div>
                    </form>
                </div>
            </Modal.Body>
        </Modal>
    )
}

export default RecordPaymentModal
